import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { SlidersHorizontal, Save, RotateCcw, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { apiGet, apiPut } from './ops_helpers';

const BOT_ID = 'hyperliquid';

// Valores por defecto = config viva actual del bot (se sobrescriben si hay config remota).
const DEFAULTS = {
  symbols: ['BTC', 'ETH', 'SOL'],
  interval: '1h',
  sma_fast: 9, sma_slow: 21,
  sl_pct: 0.025, rr_ratio: 2.5, risk_per_trade_pct: 0.01,
  leverage: 3, max_concurrent_positions: 3, max_daily_trades: 15,
  max_drawdown_stop_pct: 0.15,
  enable_regime_filter: true, regime_adx_min: 25,
  enable_rsi_filter: true, rsi_long_min: 50, rsi_long_max: 70, rsi_short_min: 30, rsi_short_max: 50,
  enable_volume_filter: true, volume_min_ratio: 1.0,
  enable_funding_filter: true,
  enable_oi_filter: true,
  enable_liquidity_filter: true,
  enable_macro_blackout: true,
  enable_htf_bias_filter: false,
  enable_trailing: false, trailing_be_at_r: 1.67,
  long_only: false,
};

// Topes de seguridad (mismos que aplica el sync del VPS antes de tocar el bot).
const LIMITS = {
  sma_fast: [2, 50, 1], sma_slow: [5, 200, 1],
  sl_pct: [0.003, 0.1, 0.001], rr_ratio: [0.5, 6, 0.1], risk_per_trade_pct: [0.001, 0.02, 0.001],
  leverage: [1, 5, 1], max_concurrent_positions: [1, 6, 1], max_daily_trades: [1, 50, 1],
  max_drawdown_stop_pct: [0.02, 0.3, 0.01],
  regime_adx_min: [5, 40, 1], volume_min_ratio: [0, 3, 0.1],
  rsi_long_min: [0, 100, 1], rsi_long_max: [0, 100, 1], rsi_short_min: [0, 100, 1], rsi_short_max: [0, 100, 1],
  trailing_be_at_r: [0.5, 5, 0.1],
};

const ALL_SYMBOLS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'AVAX', 'DOGE', 'LINK', 'SUI', 'HYPE'];
const PARAM_KEYS = Object.keys(DEFAULTS);

const clamp = (k, v) => {
  const lim = LIMITS[k];
  let n = parseFloat(v);
  if (Number.isNaN(n)) n = DEFAULTS[k];
  if (!lim) return n;
  return Math.min(lim[1], Math.max(lim[0], n));
};

const NumberField = ({ k, label, cfg, set, disabled }) => {
  const lim = LIMITS[k] || [undefined, undefined, 'any'];
  return (
    <div className="space-y-1">
      <Label className="text-xs text-zinc-400">{label}</Label>
      <Input
        type="number" min={lim[0]} max={lim[1]} step={lim[2]}
        data-testid={`hl-field-${k}`}
        value={cfg[k]}
        disabled={disabled}
        onChange={(e) => set(k, e.target.value)}
        onBlur={(e) => set(k, clamp(k, e.target.value))}
        className="bg-zinc-800 border-zinc-700 text-zinc-100 h-9"
      />
    </div>
  );
};

const ToggleRow = ({ k, label, hint, cfg, set, disabled }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <p className="text-sm text-zinc-200">{label}</p>
      {hint && <p className="text-xs text-zinc-500">{hint}</p>}
    </div>
    <Switch
      data-testid={`hl-toggle-${k}`}
      checked={!!cfg[k]}
      disabled={disabled}
      onCheckedChange={(v) => set(k, v)}
    />
  </div>
);

export const HLControlPanel = ({ isAdmin }) => {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(null);
  const [msg, setMsg] = useState(null);
  const ro = !isAdmin;

  const applyData = (ac) => {
    if (ac?.params) {
      setCfg({ ...DEFAULTS, ...ac.params, symbols: ac.params.symbols || DEFAULTS.symbols });
      setVersion(ac.version);
    } else {
      setCfg(DEFAULTS);
      setVersion(null);
    }
  };

  const reload = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const data = await apiGet(`/api/ops/config/${BOT_ID}/admin`);
      applyData(data?.active_config);
    } catch (e) {
      setMsg({ type: 'error', text: 'No se pudo cargar la config; mostrando valores por defecto.' });
      setCfg(DEFAULTS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    apiGet(`/api/ops/config/${BOT_ID}/admin`)
      .then((data) => { if (alive) applyData(data?.active_config); })
      .catch(() => {
        if (alive) {
          setMsg({ type: 'error', text: 'No se pudo cargar la config; mostrando valores por defecto.' });
          setCfg(DEFAULTS);
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const set = (k, v) => {
    setCfg({ ...cfg, [k]: v });
  };
  const toggleSymbol = (s) => {
    const has = cfg.symbols.includes(s);
    const next = has ? cfg.symbols.filter((x) => x !== s) : [...cfg.symbols, s];
    setCfg({ ...cfg, symbols: next.length ? next : ['BTC'] });
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const params = {};
      PARAM_KEYS.forEach((k) => {
        params[k] = (k in LIMITS) ? clamp(k, cfg[k]) : cfg[k];
      });
      const res = await apiPut(`/api/ops/config/${BOT_ID}`, {
        params, note: 'Editado desde panel Control HL', activate: true,
      });
      setVersion(res?.config?.version ?? (version || 0) + 1);
      setMsg({ type: 'ok', text: 'Guardado. El bot lo aplicará en ~60s (reinicio controlado).' });
    } catch (e) {
      setMsg({ type: 'error', text: `Error al guardar: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-400 p-8" data-testid="hl-control-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Cargando config de Hyperliquid…
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="hl-control-panel">
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5 text-cyan-400" /> Control de Hyperliquid
              </CardTitle>
              <CardDescription>
                Activa/desactiva filtros y calibra parámetros. Se guarda como nueva versión de config
                y el bot la aplica en ~60s (reinicio controlado, reconcilia posiciones).
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {version != null && <Badge className="bg-zinc-800 text-zinc-300" data-testid="hl-active-version">v{version} activa</Badge>}
              <Button variant="outline" size="sm" onClick={reload} disabled={saving} data-testid="hl-reload-btn"
                className="border-zinc-700 text-zinc-300">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Recargar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {msg && (
            <div data-testid="hl-control-msg" className={`text-sm rounded-md px-3 py-2 border ${
              msg.type === 'ok' ? 'bg-emerald-900/30 border-emerald-800 text-emerald-300'
                : 'bg-rose-900/30 border-rose-800 text-rose-300'}`}>
              {msg.text}
            </div>
          )}

          {/* SEÑAL & SÍMBOLOS */}
          <div>
            <p className="text-xs font-semibold text-cyan-300 uppercase tracking-wide mb-2">Señal & Símbolos</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {ALL_SYMBOLS.map((s) => {
                const on = cfg.symbols.includes(s);
                return (
                  <button key={s} type="button" disabled={ro} onClick={() => toggleSymbol(s)}
                    data-testid={`hl-symbol-${s}`}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      on ? 'bg-cyan-600/30 border-cyan-500 text-cyan-200'
                        : 'bg-zinc-800/60 border-zinc-700 text-zinc-500 hover:text-zinc-300'}`}>
                    {s}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-zinc-400">Intervalo</Label>
                <Select value={cfg.interval} onValueChange={(v) => set('interval', v)} disabled={ro}>
                  <SelectTrigger data-testid="hl-field-interval" className="bg-zinc-800 border-zinc-700 text-zinc-100 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['15m', '30m', '1h', '2h', '4h'].map((iv) => <SelectItem key={iv} value={iv}>{iv}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <NumberField k="sma_fast" label="SMA rápida" cfg={cfg} set={set} disabled={ro} />
              <NumberField k="sma_slow" label="SMA lenta" cfg={cfg} set={set} disabled={ro} />
              <div className="flex items-end">
                <ToggleRow k="long_only" label="Solo largos" cfg={cfg} set={set} disabled={ro} />
              </div>
            </div>
          </div>

          <Separator className="bg-zinc-800" />

          {/* RIESGO & TAMAÑO */}
          <div>
            <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-2">Riesgo & Tamaño (con topes de seguridad)</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <NumberField k="risk_per_trade_pct" label="Riesgo/trade (frac.)" cfg={cfg} set={set} disabled={ro} />
              <NumberField k="sl_pct" label="Stop Loss (frac.)" cfg={cfg} set={set} disabled={ro} />
              <NumberField k="rr_ratio" label="Ratio R:R" cfg={cfg} set={set} disabled={ro} />
              <NumberField k="leverage" label="Apalancamiento (máx 5)" cfg={cfg} set={set} disabled={ro} />
              <NumberField k="max_concurrent_positions" label="Máx posiciones" cfg={cfg} set={set} disabled={ro} />
              <NumberField k="max_daily_trades" label="Máx trades/día" cfg={cfg} set={set} disabled={ro} />
              <NumberField k="max_drawdown_stop_pct" label="DD stop (frac.)" cfg={cfg} set={set} disabled={ro} />
            </div>
          </div>

          <Separator className="bg-zinc-800" />

          {/* FILTROS */}
          <div>
            <p className="text-xs font-semibold text-violet-300 uppercase tracking-wide mb-2">Filtros de entrada (ON/OFF)</p>
            <div className="grid md:grid-cols-2 gap-x-8">
              <div className="divide-y divide-zinc-800/60">
                <ToggleRow k="enable_regime_filter" label="Filtro de régimen (ADX)" hint="Solo opera en tendencia fuerte — el que más bloquea" cfg={cfg} set={set} disabled={ro} />
                {cfg.enable_regime_filter && (
                  <div className="py-2"><NumberField k="regime_adx_min" label="ADX mínimo" cfg={cfg} set={set} disabled={ro} /></div>
                )}
                <ToggleRow k="enable_volume_filter" label="Filtro de volumen" hint="Exige volumen sobre la media" cfg={cfg} set={set} disabled={ro} />
                {cfg.enable_volume_filter && (
                  <div className="py-2"><NumberField k="volume_min_ratio" label="Ratio mínimo de volumen" cfg={cfg} set={set} disabled={ro} /></div>
                )}
                <ToggleRow k="enable_macro_blackout" label="Blackout macro" hint="No opera alrededor de eventos macro" cfg={cfg} set={set} disabled={ro} />
                <ToggleRow k="enable_htf_bias_filter" label="Sesgo HTF (4h)" cfg={cfg} set={set} disabled={ro} />
              </div>
              <div className="divide-y divide-zinc-800/60">
                <ToggleRow k="enable_rsi_filter" label="Filtro RSI" hint="Exige RSI dentro de banda" cfg={cfg} set={set} disabled={ro} />
                {cfg.enable_rsi_filter && (
                  <div className="grid grid-cols-2 gap-2 py-2">
                    <NumberField k="rsi_long_min" label="RSI long min" cfg={cfg} set={set} disabled={ro} />
                    <NumberField k="rsi_long_max" label="RSI long max" cfg={cfg} set={set} disabled={ro} />
                    <NumberField k="rsi_short_min" label="RSI short min" cfg={cfg} set={set} disabled={ro} />
                    <NumberField k="rsi_short_max" label="RSI short max" cfg={cfg} set={set} disabled={ro} />
                  </div>
                )}
                <ToggleRow k="enable_funding_filter" label="Filtro funding" cfg={cfg} set={set} disabled={ro} />
                <ToggleRow k="enable_oi_filter" label="Filtro Open Interest" cfg={cfg} set={set} disabled={ro} />
                <ToggleRow k="enable_liquidity_filter" label="Filtro de liquidez" cfg={cfg} set={set} disabled={ro} />
                <ToggleRow k="enable_trailing" label="Trailing stop" cfg={cfg} set={set} disabled={ro} />
                {cfg.enable_trailing && (
                  <div className="py-2"><NumberField k="trailing_be_at_r" label="Break-even a R" cfg={cfg} set={set} disabled={ro} /></div>
                )}
              </div>
            </div>
          </div>

          <Separator className="bg-zinc-800" />

          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-zinc-500 flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              Topes de seguridad aplicados (apalancamiento ≤5, riesgo ≤2%). El bot vuelve a clampar al aplicar.
            </p>
            {ro ? (
              <p className="text-xs text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Solo lectura (no eres admin)
              </p>
            ) : (
              <Button onClick={save} disabled={saving} data-testid="hl-save-btn"
                className="bg-cyan-600 hover:bg-cyan-500 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Guardar y aplicar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HLControlPanel;
